import type { IEventConfigRepository } from '@/domain/repositories/IEventConfigRepository';
import type { EventConfig } from '@/domain/entities/EventConfig';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import { DeviceSessionExpiredError } from '@/infrastructure/http/DeviceSessionExpiredError';
import { NetworkUnavailableError } from '@/infrastructure/http/NetworkUnavailableError';

export type LoadEventConfigResult =
  | { success: true; config: EventConfig }
  | { success: false; sessionExpired: true; expiredAt: Date }
  | { success: false; sessionExpired: false; offline: true }
  | { success: false; sessionExpired: false; offline: false; error: string };

/**
 * Use case responsável por carregar a configuração de ativação do evento,
 * autenticado com a sub-sessão de dispositivo emitida pelo PIN (HUB-235).
 * Encapsula o tratamento de erro da chamada ao repositório, distinguindo
 * três casos:
 * - `sessionExpired: true` — sessão expirada em uso (CA-03): o evento foi
 *   invalidado depois que o PIN foi validado
 * - `offline: true` — falha de rede pura, sem cache válido (achado do Tech
 *   Lead, review do PR #85): nunca deve virar mensagem de erro genérica,
 *   é o estado "aguardando conexão" do Design da spec
 * - `offline: false` (com `error`) — qualquer outra falha real (schema
 *   inesperado, HTTP 4xx/5xx do servidor — CA-07)
 */
export class LoadEventConfigUseCase {
  constructor(private readonly repository: IEventConfigRepository) {}

  async execute(session: DeviceSession): Promise<LoadEventConfigResult> {
    try {
      const config = await this.repository.load(session);
      return { success: true, config };
    } catch (error) {
      if (error instanceof DeviceSessionExpiredError) {
        return { success: false, sessionExpired: true, expiredAt: error.expiredAt };
      }
      if (error instanceof NetworkUnavailableError) {
        return { success: false, sessionExpired: false, offline: true };
      }
      const message =
        error instanceof Error
          ? error.message
          : 'Erro desconhecido ao carregar configuração do evento.';
      return { success: false, sessionExpired: false, offline: false, error: message };
    }
  }
}
